import { gql } from '@apollo/client';
import moment from 'moment-timezone';
import React, { FC, useEffect, useState } from 'react';

import { EmptyState } from '@sbu/components.emptystate';

import {
  StartSumoRawMessageLookupJobOperationMutation as StartSumoRawMessageLookupJobOperation,
  StartSumoRawMessageLookupJobOperationMutationVariables as StartSumoRawMessageLookupJobOperationVariables,
  SumoRawMessageLookupJobResultOperationQuery as SumoRawMessageLookupJobResultOperation,
  SumoRawMessageLookupJobResultOperationQueryVariables as SumoRawMessageLookupJobResultOperationVariables,
} from 'core/graphql';
import { RecordType } from 'shared/components/internal/Records';
import { useMutation, useQuery } from 'shared/hooks';

import { checkDoneGathering } from '../RecordSearchResults/utils';
import { SumoRawMessage } from './SumoRawMessage';

export const hasMessageId = (record: RecordType) =>
  !!record.metadata_sourceMessageId;

const START_SUMO_RAW_MESSAGE_LOOKUP_MUTATION = gql`
  mutation StartSumoRawMessageLookupJobOperation(
    $messageId: String!
    $timestamp: DateTime!
  ) {
    startSumoRawMessageLookupJob(messageId: $messageId, timestamp: $timestamp) {
      jobId
    }
  }
`;

const SUMO_RAW_MESSAGE_LOOKUP_JOB_RESULT_QUERY = gql`
  query SumoRawMessageLookupJobResultOperation(
    $searchStarted: Boolean!
    $jobId: ID!
  ) {
    sumoRecordSearchJobStatus(jobId: $jobId) @include(if: $searchStarted) {
      state
      total
    }
    sumoRawMessageLookupJobResult(jobId: $jobId) @include(if: $searchStarted)
  }
`;

interface SumoRawMessageContainerProps {
  record: RecordType;
}

export const SumoRawMessageContainer: FC<
  React.PropsWithChildren<SumoRawMessageContainerProps>
> = ({ record }) => {
  const [jobId, setJobId] = useState<string | undefined>(undefined);
  const [searchFailed, setSearchFailed] = useState(false);

  const { metadata_sourceMessageId, metadata_parseTime } = record;
  const isoTimestamp = moment(metadata_parseTime).toISOString();
  const cacheKey = `${isoTimestamp}-${metadata_sourceMessageId}`;

  const [startLookupMutation] = useMutation<
    StartSumoRawMessageLookupJobOperation,
    StartSumoRawMessageLookupJobOperationVariables
  >(START_SUMO_RAW_MESSAGE_LOOKUP_MUTATION, {
    variables: {
      messageId: metadata_sourceMessageId,
      timestamp: isoTimestamp,
    },
  });

  useEffect(() => {
    const startLookup = async () => {
      const startLookupResult = await startLookupMutation();

      if (startLookupResult?.data?.startSumoRawMessageLookupJob?.jobId) {
        setJobId(startLookupResult.data.startSumoRawMessageLookupJob.jobId);
      } else {
        setSearchFailed(true);
      }
    };

    if (!jobId && !searchFailed) {
      startLookup();
    }
  }, [
    cacheKey,
    isoTimestamp,
    jobId,
    metadata_sourceMessageId,
    searchFailed,
    startLookupMutation,
  ]);

  const { data, startPolling, stopPolling, error } = useQuery<
    SumoRawMessageLookupJobResultOperation,
    SumoRawMessageLookupJobResultOperationVariables
  >(SUMO_RAW_MESSAGE_LOOKUP_JOB_RESULT_QUERY, {
    variables: {
      searchStarted: !!jobId,
      jobId: jobId || '',
    },
    fetchPolicy: 'network-only',
  });

  const doneGathering = checkDoneGathering(data);

  const hasResults =
    !!data?.sumoRecordSearchJobStatus?.total &&
    !!data?.sumoRawMessageLookupJobResult?.length;

  const gatheringResults = !!(jobId && !doneGathering && !error && !hasResults);

  if (gatheringResults) {
    startPolling(1000);
  } else {
    stopPolling();
  }

  return !!jobId && !gatheringResults && !hasResults ? (
    <EmptyState height={200}>Record was not found</EmptyState>
  ) : (
    <SumoRawMessage
      loading={gatheringResults}
      rawMessage={data?.sumoRawMessageLookupJobResult}
    />
  );
};
